import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  NUMBER_OPS,
  TEXT_OPS,
  type ColKind,
  type FilterDraft,
  type NumberOp,
  type TextOp,
} from "@/components/clicks/clicksGridEngine";
import { cn } from "@/lib/utils";

/**
 * One column condition - the operator and its value - as the grid's header
 * popover, a condition chip and a builder row all edit it. Without onApply it
 * only edits the draft: the builder applies every row at once.
 */
export function ColumnFilterForm({ kind, draft, onDraft, onApply, onClear, inline = false, autoFocus = false }: {
  kind: ColKind;
  draft: FilterDraft;
  onDraft: (next: FilterDraft) => void;
  onApply?: () => void;
  onClear?: () => void;
  /** Operator and value on one line (a builder row). */
  inline?: boolean;
  autoFocus?: boolean;
}) {
  const needsValue = !(kind === "text" && (draft.op === "isEmpty" || draft.op === "isNotEmpty"));
  const enter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && onApply) {
      e.preventDefault();
      onApply();
    }
  };
  return (
    <div className={cn(inline ? "flex min-w-0 flex-1 items-center gap-2" : "space-y-2")}>
      <Select value={draft.op} onValueChange={(op) => onDraft({ ...draft, op: op as TextOp | NumberOp })}>
        <SelectTrigger className={cn("h-8", inline && "w-[130px] shrink-0")} aria-label="Condition">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(kind === "text" ? TEXT_OPS : NUMBER_OPS).map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {needsValue ? (
        <div className="flex min-w-0 flex-1 gap-2">
          <Input
            className="h-8"
            autoFocus={autoFocus}
            aria-label="Value"
            // The link keeps a text condition up to 2000 characters (parseColumnFilters).
            maxLength={kind === "text" ? 2000 : undefined}
            placeholder={kind === "number" && draft.op === "inList" ? "1, 2, 3" : "value"}
            value={draft.a}
            onChange={(e) => onDraft({ ...draft, a: e.target.value })}
            onKeyDown={enter}
          />
          {kind === "number" && draft.op === "between" ? (
            <Input
              className="h-8"
              aria-label="Max"
              placeholder="max"
              value={draft.b}
              onChange={(e) => onDraft({ ...draft, b: e.target.value })}
              onKeyDown={enter}
            />
          ) : null}
        </div>
      ) : null}

      {onApply ? (
        <div className="flex gap-2">
          <Button size="sm" className="h-7 flex-1" onClick={onApply}>
            Apply
          </Button>
          {onClear ? (
            <Button variant="ghost" size="sm" className="h-7 text-muted-foreground" onClick={onClear}>
              <X className="size-3.5" /> Clear
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
